import { STREET_TILES, STREET_OPTIONS } from "./mapFoundation";
import { projectPublicTripSnapshot, type ShareVisibility } from "./sharePrivacy";
export type TripShareVisibility = ShareVisibility;

export interface TripShareDisplayField {
  key: string;
  label: string;
  value: string;
  url?: string;
}

export interface TripShareDisplayItem {
  title?: string;
  fields: TripShareDisplayField[];
}

export interface TripShareDisplaySection {
  key: string;
  title: string;
  items: TripShareDisplayItem[];
}

const SECTION_LABELS: Record<string, string> = {
  usefulInfo: "Info utili",
  boardingPasses: "Carte d’imbarco",
  tickets: "Biglietti e allegati",
  documents: "Documenti",
  hotels: "Hotel",
  restaurants: "Ristoranti",
  places: "Luoghi",
  pointsOfInterest: "Punti di interesse",
  checklists: "Checklist",
  notes: "Appunti",
  itinerary: "Itinerario",
  experiences: "Esperienze",
  events: "Eventi",
  participants: "Partecipanti"
};

const FIELD_LABELS: Record<string, string> = {
  plugType: "Tipo di presa",
  currency: "Valuta",
  exchangeRate: "Cambio",
  drinkingWater: "Acqua potabile",
  taxiApps: "App taxi",
  foodDeliveryApps: "App consegna cibo",
  embassy: "Ambasciata",
  consulates: "Consolati",
  emergencyNumber: "Numero di emergenza",
  specialPrecautions: "Precauzioni particolari",
  governmentType: "Forma di governo",
  religion: "Religione",
  publicAffection: "Affetto in pubblico",
  lgbtq: "Informazioni LGBTQ+",
  requiredVaccines: "Vaccini richiesti",
  visa: "Visto",
  passport: "Passaporto",
  drivingSide: "Lato di guida",
  internationalDrivingPermit: "Patente internazionale",
  name: "Nome",
  title: "Titolo",
  date: "Data",
  startDate: "Data inizio",
  endDate: "Data fine",
  startTime: "Ora inizio",
  endTime: "Ora fine",
  time: "Ora",
  departureDate: "Data partenza",
  departureTime: "Ora partenza",
  arrivalTime: "Ora arrivo",
  boardingTime: "Imbarco",
  airline: "Compagnia",
  flightNumber: "Volo",
  passengerName: "Passeggero",
  originCode: "Origine",
  originCity: "Città di origine",
  destinationCode: "Destinazione",
  destinationCity: "Città di destinazione",
  terminal: "Terminal",
  gate: "Gate",
  seat: "Posto",
  bookingReference: "Codice prenotazione",
  city: "Città",
  address: "Indirizzo",
  type: "Tipo",
  notes: "Note",
  description: "Descrizione",
  placeName: "Luogo",
  price: "Prezzo",
  amount: "Importo",
  cost: "Costo",
  participantName: "Viaggiatore",
  participant: "Viaggiatore",
  role: "Ruolo",
  email: "Email",
  identifier: "Contatto",
  invitationStatus: "Stato invito",
  status: "Stato",
  folder: "Cartella",
  folderName: "Cartella",
  category: "Categoria",
  bookedWith: "Prenotato con",
  bookingCode: "Codice prenotazione",
  reservationCode: "Codice prenotazione",
  checkInDate: "Check-in",
  checkOutDate: "Check-out",
  checkInTime: "Ora check-in",
  checkOutTime: "Ora check-out",
  phone: "Telefono",
  website: "Sito web",
  url: "Link",
  completed: "Completato",
  checked: "Completato",
  quantity: "Quantità"
};

const HIDDEN_FIELDS = new Set([
  "id",
  "queueId",
  "storagePath",
  "createdAt",
  "updatedAt",
  "ownerUid",
  "targetUid",
  "invitationId",
  "googlePlaceId",
  "latitude",
  "longitude",
  "mimeType",
  "aiGeneratedAt",
  "source",
  "publicShareParticipant"
]);

export function filterTripShareSnapshot(snapshot: Record<string, unknown>, visibility: TripShareVisibility): Record<string, unknown> {
  return projectPublicTripSnapshot(snapshot, visibility);
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  }[char] || char));
}

function humanizeKey(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced ? `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}` : key;
}

function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Sì" : "No";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(formatPrimitive).filter(Boolean).join(", ");
  return "";
}

function isUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function itemTitle(record: Record<string, unknown>): string | undefined {
  const candidates = [record.name, record.title, record.placeName, record.airline, record.flightNumber, record.passengerName];
  for (const candidate of candidates) {
    const value = formatPrimitive(candidate);
    if (value) return value;
  }
  const origin = formatPrimitive(record.originCode || record.originCity);
  const destination = formatPrimitive(record.destinationCode || record.destinationCity);
  if (origin || destination) return [origin, destination].filter(Boolean).join(" → ");
  return undefined;
}

function fieldsFromRecord(record: Record<string, unknown>, extra?: Record<string, string>): TripShareDisplayField[] {
  const result: TripShareDisplayField[] = [];
  for (const [key, raw] of Object.entries(record)) {
    if (HIDDEN_FIELDS.has(key)) continue;
    if (key === "values" || key === "folders" || key === "items" || key === "lists") continue;
    if (raw && typeof raw === "object") continue;
    let value = formatPrimitive(raw);
    if (!value) continue;
    if (key === "size" && typeof raw === "number") {
      value = raw < 1024 * 1024 ? `${Math.max(0, Math.round(raw / 1024))} KB` : `${(raw / (1024 * 1024)).toFixed(1)} MB`;
    }
    if (key === "currency" && record.price !== null && record.price !== undefined) continue;
    if (key === "price" && raw !== null && raw !== undefined) {
      const currency = formatPrimitive(record.currency);
      value = currency ? `${value} ${currency}` : value;
    }
    result.push({ key, label: extra?.[key] || humanizeKey(key), value, ...(isUrl(value) ? { url: value } : {}) });
  }
  return result;
}

function usefulInfoItems(value: unknown): TripShareDisplayItem[] {
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  const values = record.values && typeof record.values === "object" ? record.values as Record<string, unknown> : record;
  const fields = Object.entries(values)
    .map(([key, raw]) => {
      const text = formatPrimitive(raw);
      return text ? { key, label: humanizeKey(key), value: text, ...(isUrl(text) ? { url: text } : {}) } : null;
    })
    .filter((field): field is TripShareDisplayField => Boolean(field));
  return fields.length ? [{ fields }] : [];
}

function documentItems(value: unknown): TripShareDisplayItem[] {
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  const folders = Array.isArray(record.folders) ? record.folders : [];
  const folderNames = new Map<string, string>();
  for (const folder of folders) {
    if (!folder || typeof folder !== "object") continue;
    const data = folder as Record<string, unknown>;
    const id = formatPrimitive(data.id);
    const name = formatPrimitive(data.name || data.systemKey);
    if (id && name) folderNames.set(id, name);
  }
  const items = Array.isArray(record.items) ? record.items : [];
  return items.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const data = entry as Record<string, unknown>;
    const folderId = formatPrimitive(data.folderId);
    const fields = fieldsFromRecord({
      ...data,
      folderName: folderNames.get(folderId) || folderId,
      folderId: undefined
    });
    return fields.length ? [{ title: itemTitle(data), fields }] : [];
  });
}

function checklistItems(value: unknown): TripShareDisplayItem[] {
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  const lists = Array.isArray(record.lists) ? record.lists : [];
  const listNames = new Map<string, string>();
  for (const list of lists) {
    if (!list || typeof list !== "object") continue;
    const data = list as Record<string, unknown>;
    const id = formatPrimitive(data.id);
    const name = formatPrimitive(data.name || data.title);
    if (id && name) listNames.set(id, name);
  }
  const items = Array.isArray(record.items) ? record.items : [];
  return items.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const data = entry as Record<string, unknown>;
    const listId = formatPrimitive(data.listId);
    const fields = fieldsFromRecord({ ...data, list: listNames.get(listId) || listId, listId: undefined });
    return fields.length ? [{ title: itemTitle(data), fields }] : [];
  });
}

function genericItems(value: unknown): TripShareDisplayItem[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      if (entry === null || entry === undefined) return [];
      if (typeof entry !== "object") {
        const text = formatPrimitive(entry);
        return text ? [{ fields: [{ key: "value", label: "", value: text }] }] : [];
      }
      const record = entry as Record<string, unknown>;
      const fields = fieldsFromRecord(record);
      return fields.length ? [{ title: itemTitle(record), fields }] : [];
    });
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const fields = fieldsFromRecord(record);
    return fields.length ? [{ title: itemTitle(record), fields }] : [];
  }
  const text = formatPrimitive(value);
  return text ? [{ fields: [{ key: "value", label: "", value: text }] }] : [];
}


const EXPORT_SECTION_ORDER = [
  "itinerary",
  "hotels",
  "restaurants",
  "places",
  "pointsOfInterest",
  "experiences",
  "events",
  "boardingPasses",
  "tickets",
  "documents",
  "participants",
  "checklists",
  "notes",
  "usefulInfo"
];

interface TripExportMapPoint {
  latitude: number;
  longitude: number;
  label: string;
}

function exportSectionRank(key: string): number {
  const index = EXPORT_SECTION_ORDER.indexOf(key);
  return index >= 0 ? index : EXPORT_SECTION_ORDER.length;
}

function collectTripExportMapPoints(snapshot: Record<string, unknown>): TripExportMapPoint[] {
  const points: TripExportMapPoint[] = [];
  const seen = new Set<string>();
  const sections = ["itinerary", "hotels", "restaurants", "places", "pointsOfInterest", "experiences", "events"];

  for (const section of sections) {
    const value = snapshot[section];
    if (!Array.isArray(value)) continue;
    for (const entry of value) {
      if (!entry || typeof entry !== "object") continue;
      const record = entry as Record<string, unknown>;
      const latitude = Number(record.latitude);
      const longitude = Number(record.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
      const key = `${latitude.toFixed(6)}|${longitude.toFixed(6)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      points.push({
        latitude,
        longitude,
        label: formatPrimitive(record.title || record.name || record.placeName || record.venue || record.city || record.address) || SECTION_LABELS[section] || humanizeKey(section)
      });
    }
  }
  return points;
}

function renderTripExportMap(points: TripExportMapPoint[]): string {
  if (!points.length) return `<div class="map-empty">Nessun punto geolocalizzato disponibile.</div>`;
  const data = JSON.stringify(points).replace(/</g, "\u003c");
  return `<div id="trip-map" class="trip-map"></div><script>
  window.addEventListener("DOMContentLoaded",function(){
    var points=${data};
    if(!window.L||!points.length)return;
    var map=L.map("trip-map",{scrollWheelZoom:false});
    L.tileLayer(${JSON.stringify(STREET_TILES)},${JSON.stringify(STREET_OPTIONS)}).addTo(map);
    var bounds=[];
    points.forEach(function(point){
      var marker=L.marker([point.latitude,point.longitude]).addTo(map);
      marker.bindPopup(String(point.label||"Mundely"));
      bounds.push([point.latitude,point.longitude]);
    });
    if(bounds.length===1)map.setView(bounds[0],13);else map.fitBounds(bounds,{padding:[28,28]});
  });
  </script>`;
}

export function buildTripShareDisplay(snapshot: Record<string, unknown>): TripShareDisplaySection[] {
  return Object.entries(snapshot).flatMap(([key, value]) => {
    if (key === "presentation" || key === "itineraryDays") return [];
    let items: TripShareDisplayItem[];
    if (key === "usefulInfo") items = usefulInfoItems(value);
    else if (key === "documents") items = documentItems(value);
    else if (key === "checklists") items = checklistItems(value);
    else items = genericItems(value);
    if (!items.length) return [];
    return [{ key, title: SECTION_LABELS[key] || humanizeKey(key), items }];
  });
}

function renderField(field: TripShareDisplayField): string {
  const label = field.label ? `<dt>${escapeHtml(field.label)}</dt>` : "";
  const safeValue = escapeHtml(field.value);
  const value = field.url
    ? `<dd><a href="${escapeHtml(field.url)}" target="_blank" rel="noreferrer">${safeValue}</a></dd>`
    : `<dd>${safeValue}</dd>`;
  return `${label}${value}`;
}

function renderItem(item: TripShareDisplayItem): string {
  const title = item.title ? `<h3>${escapeHtml(item.title)}</h3>` : "";
  return `<article class="item">${title}<dl>${item.fields.map(renderField).join("")}</dl></article>`;
}

export function buildTripExportHtml(trip: { name: string; destination: string; startDate: string; endDate: string }, snapshot: Record<string, unknown>): string {
  const displaySections = buildTripShareDisplay(snapshot).sort((a, b) => exportSectionRank(a.key) - exportSectionRank(b.key));
  const sections = displaySections
    .map((section) => `<section><h2>${escapeHtml(section.title)}</h2><div class="items">${section.items.map(renderItem).join("")}</div></section>`)
    .join("");
  const empty = sections ? "" : `<section><p class="empty">Nessun dato disponibile per l’esportazione.</p></section>`;
  const showMap = (snapshot.presentation as {showMap?:boolean} | undefined)?.showMap !== false;
  const map = showMap ? renderTripExportMap(collectTripExportMapPoints(snapshot)) : "";
  return `<!doctype html><html lang="it"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(trip.name)}</title><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script><style>
  :root{color-scheme:light}*{box-sizing:border-box}body{font-family:Inter,Arial,sans-serif;max-width:1440px;margin:0 auto;padding:40px 28px 70px;color:#183B3A;background:#fff}header{padding:28px 34px;border:1px solid #D8E2DC;border-radius:24px;background:linear-gradient(135deg,#FFF1D6,#FFFFFF);margin-bottom:24px}h1{margin:0 0 10px;font-size:38px;line-height:1.08}header p{margin:0;color:#526964;font-size:16px}.export-layout{display:grid;grid-template-columns:minmax(0,1fr) minmax(320px,420px);gap:26px;align-items:start}.export-content{min-width:0}.export-map-panel{position:sticky;top:24px;border:1px solid #D8E2DC;border-radius:22px;background:#fff;padding:14px;box-shadow:0 6px 24px rgba(30,25,32,.05)}.export-map-panel h2{margin:2px 2px 12px;font-size:20px}.trip-map,.map-empty{width:100%;height:620px;border-radius:16px;overflow:hidden;background:#F7F5EF}.map-empty{display:grid;place-items:center;padding:24px;text-align:center;color:#526964}section{padding:26px 0;border-bottom:1px solid #F7F5EF}section:first-child{padding-top:0}h2{margin:0 0 16px;font-size:24px}.items{display:grid;gap:12px}.item{padding:16px 18px;border:1px solid #D8E2DC;border-radius:16px;background:#fff}.item h3{margin:0 0 12px;font-size:17px}dl{display:grid;grid-template-columns:minmax(150px,220px) minmax(0,1fr);gap:8px 16px;margin:0}dt{font-weight:750;color:#526964}dd{margin:0;overflow-wrap:anywhere}a{color:#124848;text-decoration:none}a:hover{text-decoration:underline}.empty{color:#526964}@media(max-width:900px){body{padding:24px 16px 50px}.export-layout{grid-template-columns:1fr}.export-map-panel{position:static;order:-1}.trip-map,.map-empty{height:340px}h1{font-size:30px}dl{grid-template-columns:1fr;gap:3px}dd{margin:0 0 8px}.item{padding:14px}}@media print{body{max-width:none;padding:0 8mm}.export-layout{grid-template-columns:minmax(0,1fr) 36%}.export-map-panel{position:static}.trip-map,.map-empty{height:180mm}.item{break-inside:avoid}a{color:inherit;text-decoration:none}}
  </style></head><body><header><h1>${escapeHtml(trip.name)}</h1><p>${escapeHtml(trip.destination)} · ${escapeHtml(trip.startDate)} → ${escapeHtml(trip.endDate)}</p></header><div class="export-layout"><main class="export-content">${sections}${empty}</main>${showMap ? `<aside class="export-map-panel"><h2>Mappa</h2>${map}</aside>` : ""}</div></body></html>`;
}
